import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Download, Share2, Smartphone } from 'lucide-react';
import type { UsePwaInstallResult } from '@/hooks/usePwaInstall';
import { trackProductEvent } from '@/lib/productAnalytics';
import { getPwaInstallCardVariant } from './pwaInstallCardState';

export function PwaInstallCard({
  hasGeneratedPdf,
  install,
}: {
  hasGeneratedPdf: boolean;
  install: UsePwaInstallResult;
}) {
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [hideInstalledCard, setHideInstalledCard] = useState(false);
  const hasTrackedSeen = useRef(false);
  const variant = getPwaInstallCardVariant({
    hasGeneratedPdf,
    canInstall: install.canInstall,
    isInstalled: install.isInstalled,
    isIos: install.isIos,
    isStandalone: install.isStandalone,
  });

  useEffect(() => {
    if (variant !== 'hidden' && !hasTrackedSeen.current) {
      hasTrackedSeen.current = true;
      trackProductEvent('pwa_install_cta_seen');
    }
  }, [variant]);

  if (variant === 'hidden' || (variant === 'installed' && hideInstalledCard)) return null;

  if (variant === 'installed') {
    return (
      <section className="mt-4 rounded-2xl border border-[#00e676]/35 bg-[#00e676]/10 p-4 shadow-lg shadow-black/20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-[#00e676]" aria-hidden="true" />
            <div>
              <h3 className="text-base font-black text-white">Gotowe. ProWycena jest na ekranie głównym.</h3>
              <p className="mt-1 text-sm font-semibold text-slate-300">Od teraz otwierasz wyceny jak zwykłą aplikację.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setHideInstalledCard(true);
            }}
            className="min-h-14 rounded-2xl bg-[#00e676] px-5 text-base font-black text-black transition hover:bg-emerald-300"
          >
            Wróć do wyceny
          </button>
        </div>
      </section>
    );
  }

  const isIos = variant === 'ios';

  return (
    <section className="mt-4 rounded-2xl border border-amber-400/35 bg-amber-500/10 p-4 shadow-lg shadow-black/20">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <Smartphone className="mt-0.5 h-6 w-6 shrink-0 text-amber-200" aria-hidden="true" />
          <div>
            <h3 className="text-base font-black text-white">
              {isIos ? 'Dodaj ProWycenę na iPhonie' : 'ProWycena na ekranie głównym'}
            </h3>
            <p className="mt-1 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              {isIos
                ? 'Otwórz stronę w Safari, wybierz Udostępnij → Dodaj do ekranu początkowego → Otwórz jako aplikację → Dodaj.'
                : 'Wracaj do wycen jednym dotknięciem. Aplikacja otworzy się bez paska przeglądarki.'}
            </p>
          </div>
        </div>
        {isIos ? (
          <button
            type="button"
            onClick={() => {
              setShowIosSteps(true);
              trackProductEvent('pwa_install_cta_clicked');
              trackProductEvent('ios_install_instructions_seen');
            }}
            className="flex min-h-14 items-center justify-center gap-2 rounded-2xl border border-amber-300/40 bg-[#0f1217] px-5 text-base font-black text-amber-100 transition hover:bg-amber-500/10"
          >
            <Share2 className="h-5 w-5" aria-hidden="true" />
            Pokaż instrukcję
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void install.promptInstall()}
            className="flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-[#00e676] px-5 text-base font-black text-black transition hover:bg-emerald-300"
          >
            <Download className="h-5 w-5" aria-hidden="true" />
            Dodaj aplikację
          </button>
        )}
      </div>
      {isIos && showIosSteps ? (
        <ol className="mt-4 grid gap-2 rounded-xl border border-amber-300/25 bg-[#0f1217] p-4 text-sm font-semibold text-slate-200">
          <li>1. Otwórz ProWycenę w Safari.</li>
          <li>2. Wybierz Udostępnij.</li>
          <li>3. Wybierz Dodaj do ekranu początkowego.</li>
          <li>4. Włącz Otwórz jako aplikację i wybierz Dodaj.</li>
        </ol>
      ) : null}
    </section>
  );
}
